import { Component, Input, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { MatTableDataSource } from '@angular/material/table';
import { Order } from '../../interfaces/orders/order';
import { UltimateBeneficialOwnershipStructure } from '../../interfaces/ultimate-beneficial-ownership-structure/ultimate-beneficial-ownership-structure';
import { UltimateBeneficialOwnershipStructureService } from '../../services/ultimate-beneficial-ownership-structure/ultimate-beneficial-ownership-structure.service';
import { UltimateBeneficialOwnershipRequestDocument } from '../../interfaces/ultimate-beneficial-ownership-request-documents/ultimate-beneficial-ownership-request-document';
import { DocumentsRepositoryService } from '../../services/document/documents-repository.service';
import { eDocumentType, ePartyType } from '../../enums';
import { base64StringToBlob } from 'blob-util';

@Component({
    selector: 'lib-ubo-report-details',
    templateUrl: './ubo-report-details.component.html',
    styleUrl: './ubo-report-details.component.css',
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class UboReportDetailsComponent {
  protected results: unknown | null = null;
  @Input() public order!: Order;

  displayedDocColumns = ['name', 'type', 'actions'];

  displayedColumns = [
    'name',
    'address',
    'businessNumber',
    'dateOfBirth',
    'ownershipPercentage'
  ];

  dataSource = new MatTableDataSource<UltimateBeneficialOwnershipStructure>([]);
  isLoadingOwnershipStructure = false;
  private _downloading = new Set<string>();

  constructor(private _structureService: UltimateBeneficialOwnershipStructureService,
    private documentsRepo: DocumentsRepositoryService
  ) { }

  ngOnInit(): void {
    if (!this.order.ultimateBeneficialOwnershipRequest?.id) return;
    this.isLoadingOwnershipStructure = true;
    this._structureService
      .getUltimateBeneficialOwnershipStructures(this.order.ultimateBeneficialOwnershipRequest.id)
      .subscribe({
        next: (rows) => {
          this.dataSource = new MatTableDataSource(rows ?? []);
        },
        error: () => console.log('Failed to load shareholders'),
        complete: () => (this.isLoadingOwnershipStructure = false),
      });
  }

  isDownloading(id: string | undefined): boolean {
    if (!id) return false;
    return this._downloading.has(id);
  }

  onRowDownload(rowId: string) {
    const row = this.findRequestRow(rowId);
    const docId = row?.document?.id;
    if (!docId) return;

    this._downloading.add(rowId);

    this.documentsRepo.getDocument(docId).subscribe({
      next: (result) => {
        const typeId = (result?.documentTypeID || 'PDF').toString().toLowerCase();

        // Map to MIME using your enum
        const mimeMap: Record<string, string> = {
          [eDocumentType.pdf]: 'application/pdf',
          [eDocumentType.json]: 'application/json',
          [eDocumentType.xml]: 'application/xml',
          [eDocumentType.html]: 'text/html',
          [eDocumentType.csv]: 'text/csv',
          [eDocumentType.text]: 'text/plain'
        } as any;

        const mime = mimeMap[typeId] ?? 'application/pdf';

        const blob = base64StringToBlob(result.base64, mime);
        const url = URL.createObjectURL(blob);

        const a = document.createElement('a');
        a.href = url;
        a.download = row?.document?.fileName || `document.${typeId}`;
        a.click();
        URL.revokeObjectURL(url);

        this._downloading.delete(rowId);
      },
      error: () => {
        this._downloading.delete(rowId);
      }
    });
  }

  private findRequestRow(id: string): UltimateBeneficialOwnershipRequestDocument | undefined {
    return this.order.ultimateBeneficialOwnershipRequest?.documents
      ?.find(d => d.id === id);
  }

  formatName(structure: UltimateBeneficialOwnershipStructure) {
    var name: string | undefined = "";
    if (structure.party.partyTypeID == ePartyType.businessBeneficialOwner) {
      name = structure.party.busName!;
    } else {
      name = (structure.party.middleName)
        ? structure.party.firstName + " " + structure.party.middleName + " " + structure.party.lastName
        : structure.party.firstName + " " + structure.party.lastName;
    }

    return name?.trim();
  }

  formatAddress(structure: UltimateBeneficialOwnershipStructure) {
    var address = structure.party.contactDetails?.address?.unit ?? "" + ' ' +
      structure.party.contactDetails?.address?.streetNumber + ' ' +
      structure.party.contactDetails?.address?.streetName + ', ' +
      structure.party.contactDetails?.address?.city + ' ' + structure.party.contactDetails?.address?.jurisdiction + ', ' +
      structure.party.contactDetails?.address?.postalCode;

    if (address.startsWith(',') && address.length > 1) {
      address = address.substring(1);
    }

    if (address.endsWith(',')) {
      address = address.substring(1, address.length - 2);
    }

    return address.trim();
  }
}
